import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { linkDeRegisto, queryDoTipo } from "@/Logigate/components/planos/selecao";
import TipoEmpresa from "@/utils/logigate/TipoEmpresa";

const PARA_QUEM = [
    { icon: "mdi:school-outline", texto: "Consultores e formadores" },
    { icon: "mdi:tools", texto: "Electricistas, canalizadores e mecânicos" },
    { icon: "mdi:palette-outline", texto: "Designers, fotógrafos e criativos" },
    { icon: "mdi:scale-balance", texto: "Advogados, contabilistas e arquitectos" },
    { icon: "mdi:laptop", texto: "Programadores e freelancers digitais" },
    { icon: "mdi:account-heart-outline", texto: "Explicadores, treinadores e serviços à pessoa" },
];

const PASSOS = [
    {
        number: "01",
        icon: "mdi:account-plus-outline",
        title: "Cria a conta de prestador",
        description: "Regista-te na Honga Yetu, escolhe «Prestador de serviços» e preenche os teus dados: nome, NIF, contactos e morada. O teu estabelecimento é criado automaticamente.",
    },
    {
        number: "02",
        icon: "mdi:package-variant-closed",
        title: "Escolhe o plano",
        description: "Básico, Médio ou Avançado, conforme quantos documentos emite por mês. A factura do plano paga-se na hora: tem 1 hora para pagar, e a conta fica activa assim que o pagamento for confirmado.",
    },
    {
        number: "03",
        icon: "mdi:receipt-text-outline",
        title: "Regista os serviços e emite facturas",
        description: "Cria a lista dos serviços que prestas e emite facturas e facturas-recibo certificadas pela AGT, em segundos, no computador ou no telemóvel.",
    },
    {
        number: "04",
        icon: "mdi:chart-line",
        title: "Controla o que entra e sai",
        description: "Regista as entradas e as saídas da tesouraria e vê nas estatísticas quanto facturaste e quanto recebeste em cada mês.",
    },
];

const VANTAGENS = [
    {
        icon: "mdi:cash-multiple",
        title: "A partir de 1.000 Kz por mês",
        description: "Um preço pensado para quem está a começar ou factura pouco. Sem custos escondidos: o IVA já está incluído.",
    },
    {
        icon: "mdi:shield-check-outline",
        title: "Facturas certificadas pela AGT",
        description: "Facturas, facturas-recibo e notas de crédito válidas, emitidas pelo mesmo motor de facturação electrónica que serve as empresas.",
    },
    {
        icon: "mdi:filter-variant",
        title: "Só o essencial",
        description: "Sem produtos, armazém, encomendas ou estabelecimentos. Um menu curto, onde tudo o que vê é para usar.",
    },
    {
        icon: "mdi:rocket-launch-outline",
        title: "Pronto em minutos",
        description: "Não há estabelecimentos nem unidades para configurar: cria a conta, escolhe o plano e passa a primeira factura.",
    },
    {
        icon: "mdi:bank-outline",
        title: "Tesouraria simples",
        description: "Entradas e saídas registadas num só sítio, para saber sempre quanto tem e quanto ganhou.",
    },
    {
        icon: "mdi:chart-box-outline",
        title: "Estatísticas do teu negócio",
        description: "Facturação por mês, clientes que mais compram, serviços mais vendidos. Números claros para decidir melhor.",
    },
];

const INCLUIDO = [
    "Facturas (FT) e facturas-recibo (FR)",
    "Outros documentos: proformas, recibos, notas de crédito e orçamentos",
    "Lista de serviços e de clientes",
    "Tesouraria: entradas e saídas",
    "Estatísticas de facturação",
    "Acesso no computador e no telemóvel",
];

const NAO_INCLUIDO = [
    "Produtos, stock e códigos de barras",
    "Encomendas e guias",
    "Vários estabelecimentos",
    "Unidades, supervisores e aplicações",
    "Os outros módulos (Recursos Humanos, POS, Gestão Documental…)",
];

function scrollTo(href: string) {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

interface Props {
    /** A âncora da secção, para o menu da página dedicada. */
    id?: string;
}

/**
 * A explicação da conta de prestador de serviços: para quem é, como funciona,
 * porquê, e o que fica de fora (que é o que separa esta conta da de empresa).
 */
export default function PrestadoresComoFuncionaSection({ id }: Props) {
    const linkPrestador = linkDeRegisto(queryDoTipo(TipoEmpresa.PRESTADOR));

    return (
        <section id={id} className="py-20 lg:py-28 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Cabeçalho */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Como funciona o Logigate para prestadores</h2>
                    <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                        Uma conta feita à medida de quem vende o seu trabalho: sem produtos, sem armazém, sem estabelecimentos para gerir. Só o que precisas para facturar e saber quanto ganhaste.
                    </p>
                </motion.div>

                {/* Para quem é */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-20">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Para quem é</h3>
                    <p className="text-gray-500 text-center mb-8">Para quem trabalha em nome individual e precisa de passar factura aos clientes:</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto">
                        {PARA_QUEM.map((item) => (
                            <div key={item.texto} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                                <span className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                    <Icon icon={item.icon} width={20} className="text-blue-600" />
                                </span>
                                <span className="text-sm font-medium text-gray-700">{item.texto}</span>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Em quatro passos */}
                <div className="mb-20">
                    <h3 className="text-xl font-bold text-gray-900 mb-10 text-center">Em quatro passos</h3>
                    <div className="grid md:grid-cols-4 gap-8 relative">
                        <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-blue-200" />
                        {PASSOS.map((passo, index) => (
                            <motion.div
                                key={passo.number}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.12 }}
                                className="text-center relative"
                            >
                                <div className="relative inline-flex mb-5">
                                    <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                                        <Icon icon={passo.icon} width={30} className="text-white" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white text-blue-700 text-xs font-bold flex items-center justify-center shadow border border-blue-100">{passo.number}</div>
                                </div>
                                <h4 className="text-base font-semibold text-gray-900 mb-2">{passo.title}</h4>
                                <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">{passo.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Vantagens */}
                <div className="mb-20">
                    <h3 className="text-xl font-bold text-gray-900 mb-10 text-center">Porquê uma conta de prestador?</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {VANTAGENS.map((vantagem, index) => (
                            <motion.div
                                key={vantagem.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.08 }}
                                className="group rounded-2xl border border-gray-200 bg-white p-6"
                            >
                                <div className="w-14 h-14 rounded-2xl bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center mb-4 transition-colors">
                                    <Icon icon={vantagem.icon} width={28} className="text-blue-600" />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">{vantagem.title}</h4>
                                <p className="text-sm text-gray-500 leading-relaxed">{vantagem.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Incluído / não incluído */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-12">
                    <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-gray-200 bg-white p-6">
                            <h4 className="text-base font-semibold text-gray-900 mb-4">O que está incluído</h4>
                            <ul className="space-y-2.5">
                                {INCLUIDO.map((item) => (
                                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                                        <Icon icon="mdi:check-circle" width={20} className="text-green-500 shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="rounded-2xl border border-gray-200 bg-white p-6">
                            <h4 className="text-base font-semibold text-gray-900 mb-4">O que fica para as contas de empresa</h4>
                            <ul className="space-y-2.5">
                                {NAO_INCLUIDO.map((item) => (
                                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500">
                                        <Icon icon="mdi:close-circle-outline" width={20} className="text-gray-300 shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <p className="text-center text-sm text-gray-500 mt-4">
                        Precisa de algum destes?{" "}
                        <a href="/criar" className="text-blue-600 font-medium hover:underline">
                            Cria antes uma conta de empresa
                        </a>
                        .
                    </p>
                </motion.div>

                {/* Chamadas */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a href={linkPrestador} className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors">
                        <Icon icon="mdi:account-check-outline" width={20} />
                        Criar conta de prestador
                    </a>
                    <button
                        type="button"
                        onClick={() => scrollTo("#prestadores")}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-100 text-gray-800 font-semibold rounded-xl border border-gray-200 transition-colors"
                    >
                        Ver os planos
                        <Icon icon="mdi:arrow-up" width={20} />
                    </button>
                </div>
            </div>
        </section>
    );
}
